/**
 * Credenciamento de patrocinador SBF 2026 (convite por token).
 *
 * - `validatePatrocinadorInviteToken`: POST `/credenciamento-patrocinador-2026-validar-token`
 * - `submitCredenciamentoPatrocinador2026`: POST `/credenciamento-patrocinador-2026-concluir` (multipart)
 */

import api from "../../../services/axios";

/**
 * @typedef {object} ValidatePatrocinadorTokenOk
 * @property {true} ok
 * @property {string} mensagem
 * @property {{ empresa: string; email?: string; token?: string }} [dados]
 *
 * @typedef {object} ValidatePatrocinadorTokenErr
 * @property {false} ok
 * @property {string} mensagem
 *
 * @param {string | undefined} token — token da URL
 * @returns {Promise<ValidatePatrocinadorTokenOk | ValidatePatrocinadorTokenErr>}
 */
export async function validatePatrocinadorInviteToken(token) {
  const t = decodeURIComponent(String(token ?? "").trim());
  if (!t) {
    return {
      ok: false,
      mensagem: "Este link não contém um token de convite válido.",
    };
  }

  try {
    const res = await api.post("/credenciamento-patrocinador-2026-validar-token", {
      token: t,
    }, { skipAuth: true });
    if (res.status === 200 && res.data) {
      return res.data;
    }
  } catch (error) {
    const msg =
      error.response?.data?.mensagem ||
      error.response?.data?.message ||
      "Não foi possível validar o convite.";
    return { ok: false, mensagem: msg };
  }

  return {
    ok: false,
    mensagem: "Resposta inválida ao validar o convite.",
  };
}

/**
 * @param {object} params
 * @param {string | undefined} params.inviteToken
 * @param {string} params.nomeCracha
 * @param {string} params.email
 * @param {string} params.cpf
 * @param {string} params.dataNascimento
 * @param {string} params.sexo
 * @param {string} params.telComercial
 * @param {string} params.celular
 * @param {string} params.cargo
 * @param {string} params.acaoEspecial — "sim" ou "nao"
 * @param {boolean} params.usoImagem
 * @param {string} params.bioCurta
 * @param {boolean} params.aceiteRegulamento
 * @param {File | null | undefined} params.bioFoto
 * @returns {Promise<{ ok: boolean; protocolo?: string; token?: string; url?: string; mensagem: string }>}
 */
export async function submitCredenciamentoPatrocinador2026(params) {
  const token = decodeURIComponent(String(params.inviteToken ?? "").trim());
  if (!token) {
    return {
      ok: false,
      mensagem: "Token de convite ausente.",
    };
  }

  const foto = params.bioFoto;
  if (!foto || !(foto instanceof File)) {
    return {
      ok: false,
      mensagem: "É necessário enviar a foto de perfil.",
    };
  }

  const fd = new FormData();
  fd.append("inviteToken", token);
  fd.append("nomeCracha", String(params.nomeCracha ?? ""));
  fd.append("email", String(params.email ?? "").trim());
  fd.append("cpf", String(params.cpf ?? ""));
  fd.append("dataNascimento", String(params.dataNascimento ?? ""));
  fd.append("sexo", String(params.sexo ?? ""));
  fd.append("telComercial", String(params.telComercial ?? ""));
  fd.append("celular", String(params.celular ?? ""));
  fd.append("cargo", String(params.cargo ?? ""));
  fd.append("bioCurta", String(params.bioCurta ?? ""));
  fd.append("acaoEspecial", params.acaoEspecial === "sim" ? "true" : "false");
  fd.append("usoImagem", params.usoImagem ? "true" : "false");
  fd.append("aceiteRegulamento", params.aceiteRegulamento ? "true" : "false");
  fd.append("avatar", foto, foto.name);

  try {
    const res = await api.post("/credenciamento-patrocinador-2026-concluir", fd, {
      skipAuth: true,
    });
    const data = res.data ?? {};
    if (res.status === 200 && typeof data.ok === "boolean") {
      return {
        ok: data.ok,
        protocolo: data.protocolo,
        token: data.token,
        url: data.url,
        mensagem:
          data.mensagem ||
          (data.ok
            ? "Credenciamento concluído com sucesso."
            : "Não foi possível concluir o credenciamento."),
      };
    }
  } catch (error) {
    const data = error.response?.data;
    const msg =
      data?.mensagem ||
      data?.message ||
      "Não foi possível concluir o credenciamento. Tente novamente.";
    return { ok: false, mensagem: msg };
  }

  return {
    ok: false,
    mensagem: "Resposta inválida da API ao concluir o credenciamento.",
  };
}
